import {
  Component,
  ViewChild,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { TruckDialogComponent } from '@app/trucks/truck-dialog/truck-dialog.component';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DatePipe, CommonModule } from '@angular/common';
import { MatChip } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HostListener } from '@angular/core';

@Component({
  selector: 'app-trucks',
  standalone: true,
  imports: [
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    RouterModule,
    MatPaginatorModule,
    MatSortModule,
    DatePipe,
    MatChip,
    CommonModule,
    MatMenuModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './trucks.component.html',
  styleUrl: './trucks.component.scss',
  providers: [DatePipe],
})
export class TrucksComponent implements OnInit, AfterViewInit {

  isMobile = window.innerWidth < 768;
  itemsPerPage = this.isMobile ? 10 : 25;

  desktopColumns: string[] = [
    'refreshed_since',
    'available_start_date',
    'origin_city',
    'origin_state',
    'axle_count',
    'max_weight',
    'carrier_name',
    'reference',
    'visible',
    'actions',
  ];

  mobileColumns = [
    'mobile'
  ];

  displayedColumns = this.isMobile ? this.mobileColumns : this.desktopColumns;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
    this.displayedColumns = this.isMobile
      ? this.mobileColumns
      : this.desktopColumns;
  }

  dataSource = new MatTableDataSource<any>([]);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  loading = false;

  constructor(
    private router: Router,
    private appService: AppService,
    private authService: AuthService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (!this.authService.userCanPostTrucks()) {
      this.appService.navigateToDefaultUrl();
      return;
    }

    this.getMyTrucks();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getMyTrucks(successMessage: boolean = false): void {
    this.loading = true;
    this.appService.getMyTrucks().subscribe({
      next: (success) => {
        this.dataSource.data = success.data || [];

        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        if (successMessage) {
          this.appService.showSnackBar(success.message, 'success');
        }
        this.loading = false;
      },
      error: (err) => {
        this.dataSource.data = [];
        this.appService.handleError(err);
        this.loading = false;
      },
    });
  }

  createTruck(): void {
    this.openTruckDialog();
  }

  updateTruck(truck: any): void {
    this.openTruckDialog(truck);
  }

  copyTruck(truck: any): void {
    this.openTruckDialog(truck, true);
  }

  openTruckDialog(truck: any | null = null, copy: boolean = false): void {
    let mode = 'add';

    if (truck) {
      mode = copy ? 'add' : 'edit';
    }

    const dialogRef = this.dialog.open(TruckDialogComponent, {
      width: '750px',
      maxWidth: '750px',
      data: {
        truck: truck ? truck : null,
        mode: mode,
        display: 'dialog',
        copy: copy,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getMyTrucks();
      }
    });
  }

  refreshTruck(truck: any): void {
    const truckRefreshLimit = 30;
    const now = new Date();
    const lastRefreshed = new Date(truck.refreshed_at);

    const timeDiff = Math.round(
      (now.getTime() - lastRefreshed.getTime()) / (1000 * 60)
    );

    const refreshIn = truckRefreshLimit - timeDiff;

    if (timeDiff < truckRefreshLimit) {
      this.appService.showSnackBar(
        `You can refresh this truck in ${refreshIn} minutes.`,
        'warning'
      );
      return;
    }

    this.appService.refreshTruck(truck.id).subscribe({
      next: (success) => {
        this.appService.showSnackBar(success.message, 'success');
        this.getMyTrucks();
      },
      error: (err) => {
        this.appService.handleError(err);
      },
    });
  }

  toggleVisibility(truck: any): void {
    const originalVisible = truck.visible;
    const truckId = truck.id;
    const newVisible = !originalVisible;

    this.updateLocalVisibility(truckId, newVisible);

    this.appService.toggleTruckVisibility(truckId).subscribe({
      next: (response) => {
        this.appService.showSnackBar(response.message, 'success');
      },
      error: (err) => {
        this.updateLocalVisibility(truckId, originalVisible);
        this.appService.handleError(err);
      },
    });
  }

  private updateLocalVisibility(truckId: number, newVisible: boolean): void {
    const updated = this.dataSource.data.map((truck: any) =>
      truck.id === truckId ? { ...truck, visible: newVisible } : truck
    );

    this.dataSource.data = updated;
  }

  deleteTruck(truck: any): void {
    this.appService
      .confirm('Are you sure you want to delete this truck?')
      .subscribe((result) => {
        if (!result) return;

        this.appService.delete(`trucks/${truck.id}`).subscribe({
          next: (response) => {
            this.appService.showSnackBar(response.message, 'success');

            this.dataSource.data = this.dataSource.data.filter(
              (item: any) => item.id !== truck.id
            );
          },
          error: (err) => {
            this.appService.handleError(err);
          },
        });
      });
  }

  matchLoad(truck: any): void {
    this.router.navigate(['/loads/searches'], {
      state: { truckToMatch: truck },
    });
  }

  formatAge(refreshedSince: string): string {
    if (!refreshedSince) {
      return '';
    }

    const [hours, minutes] = refreshedSince.split(':').map(Number);

    if (hours < 24) {
      return refreshedSince;
    }

    const days = Math.floor(hours / 24);

    return `${days} day${days > 1 ? 's' : ''}`;
  }

  isFresh(item: any): boolean {
    if (!item.refreshed_since) {
      return false;
    }

    const [hours, minutes] = item.refreshed_since.split(':').map(Number);

    return hours === 0 && minutes < 5;
  }

  isVisible(truck: any): boolean {
    return truck.visible;
  }

  toFeetAndInches(totalInches: number): string {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

}